/**
 * The one place parcels are built from contents.
 *
 * Before SC-2108's consolidation this arithmetic existed five times over
 * (SingleOrder, MultiOrderMagically, RedistributeConsignmentParcels, Custom's seed and Combining's
 * one-parcel seed), each with slightly different rounding. `distributeIntoParcels` replaces all
 * five. Two operations stay separate because they are genuinely different, not duplicates:
 *
 *   - `replicateIntoParcels` copies every row into every parcel and only divides Value. Nothing is
 *     allocated. This is what "one item, too heavy for one box" needs.
 *   - `splitValueAcross` divides a total across caller-supplied per-box quantities, for the flow
 *     that lets the user type quantities directly.
 *
 * TWO DELIBERATE OVERFLOW CONTRACTS, preserved from the pre-consolidation code:
 *
 *   'pad-with-copies' - asking for more parcels than units gives every parcel a quantity-1 copy, so
 *                       DECLARED QUANTITY EXCEEDS ORDERED QUANTITY. Value is still conserved. This
 *                       is intentional: one physical item may genuinely need several boxes, and the
 *                       user is expected to redescribe the rows. It is why the consignment parcel
 *                       picker floors its option count rather than capping at the unit count.
 *   'clamp'           - never exceed the piece count. Correct when there are several lines, because
 *                       there is no way to know which line a surplus parcel would be for.
 *
 * ROUNDING: QuantityValueSummary returns unrounded doubles - QuantityValueSummary(100, 3)
 * .valueForFirstItem is 33.339999999999996 - and the old code accumulated those with `+=`, so float
 * dust reached the API as customs declaration values. Every Value this module emits goes through
 * round2. Totals are still conserved to 2dp.
 */
import {
  ConsignmentApiModel,
  ConsignmentParcelItemApiModel,
  MarketplaceOrderApiModel
} from '~/lib/api/smartsend/types';
import { tinyId } from '../helpers/uid';
import { QuantitySummary } from '../../components/smartsend/panels/QuantitySummary';
import { QuantityValueSummary } from '../../components/smartsend/panels/QuantityValueSummary';
import {
  ConsolidateParcelQuantities,
  SplitConsignmentWeight
} from '../../components/smartsend/panels/consignments/GetConsignmentFromItem';
import { MapMarketplaceOrderlineToConsignmentParcel } from '../../components/smartsend/grouping/MapMarketplaceOrderlineToConsignmentParcel';
import { DistributeOptions } from './types';
import { round2 } from './round';

// Re-exported so the many existing `import { round2 } from './allocate'` call sites keep working; the
// definition moved to a leaf module because QuantityValueSummary needs it and cannot import from here.
export { round2 } from './round';

const sum = (ns: number[]) => ns.reduce((a, b) => a + b, 0);

/** One content row per order line, preserving the line's own Quantity and Value. */
export function linesToContents(lines: MarketplaceOrderApiModel[]): ConsignmentParcelItemApiModel[] {
  return lines.map((line) => ({
    ...MapMarketplaceOrderlineToConsignmentParcel(line),
    Id: line.Id,
    UID: tinyId(),
    Value: round2(line.Value)
  }));
}

/**
 * Explodes quantity-N rows into N quantity-1 rows, dividing Value with the house convention (the
 * first unit absorbs the rounding remainder). A row with no quantity counts as one unit, matching
 * the `Quantity || 1` coercion the old redistribute path relied on.
 */
export function flattenToUnits(rows: ConsignmentParcelItemApiModel[]): ConsignmentParcelItemApiModel[] {
  const units: ConsignmentParcelItemApiModel[] = [];

  rows.forEach((row) => {
    const quantity = row.Quantity || 1;
    const split = QuantityValueSummary(row.Value, quantity);

    for (let index = 0; index < quantity; index++) {
      units.push({
        ...row,
        Quantity: 1,
        UID: tinyId(),
        Value: round2(index === 0 ? split.valueForFirstItem : split.valueForOtherItems)
      });
    }
  });

  return units;
}

const buildParcels = (count: number, opts: DistributeOptions): ConsignmentApiModel => ({
  Parcels: Array.from({ length: count }, (_, index) => ({
    Contents: [],
    // Spread so parcels never share a Dimensions reference - editing one must not change the rest.
    Dimensions: { ...opts.dimensions },
    UID: tinyId(),
    Id: opts.parcelId(index),
    CanAdd: opts.canAdd ?? true
  }))
});

/**
 * Distributes `contents` across `parcelCount` parcels. See the module docblock for the two overflow
 * contracts. Returns an empty plan when there is nothing to allocate or no parcels were asked for,
 * which every consumer treats as "send as a single parcel".
 */
export function distributeIntoParcels(
  contents: ConsignmentParcelItemApiModel[],
  parcelCount: number,
  opts: DistributeOptions
): ConsignmentApiModel {
  if (parcelCount <= 0 || contents.length === 0) {
    return { Parcels: [] };
  }

  if (opts.granularity === 'rows') {
    const count = Math.min(parcelCount, contents.length);
    const plan = buildParcels(count, opts);
    contents.forEach((row, index) => plan.Parcels[index % count].Contents.push(row));
    return finalise(plan, opts);
  }

  const units = flattenToUnits(contents);

  // Fewer units than parcels, and the caller wants the requested count honoured: give every parcel
  // its own quantity-1 copy and divide the total value by the PARCEL count, not the unit count.
  if (opts.overflow === 'pad-with-copies' && units.length < parcelCount) {
    const plan = buildParcels(parcelCount, opts);
    const split = QuantityValueSummary(sum(contents.map((c) => c.Value)), parcelCount);

    plan.Parcels.forEach((parcel, index) => {
      parcel.Contents.push({
        ...contents[0],
        Quantity: 1,
        UID: tinyId(),
        Value: round2(index === 0 ? split.valueForFirstItem : split.valueForOtherItems)
      });
    });

    return finalise(plan, opts);
  }

  const count = Math.min(parcelCount, units.length);
  const plan = buildParcels(count, opts);
  const perParcel = QuantitySummary(units.length, count);

  let unitIndex = 0;
  plan.Parcels.forEach((parcel, parcelIndex) => {
    for (let i = 0; i < perParcel[parcelIndex]; i++) {
      parcel.Contents.push(units[unitIndex]);
      unitIndex++;
    }
  });

  // Merge the quantity-1 units back into one row per distinct product per parcel.
  ConsolidateParcelQuantities(plan);
  plan.Parcels.forEach((p) => p.Contents.forEach((o) => (o.Value = round2(o.Value))));

  return finalise(plan, opts);
}

/**
 * Every parcel gets a COPY of every row, with each row's Value divided across the parcels. Nothing
 * is allocated - this is a replicated declaration, for splitting one physical item into boxes.
 *
 * Note that Quantity is carried through unchanged, so replicating a quantity-N row into M boxes
 * declares N units in each. Preserved as-is: the only flow that uses this is reachable only for
 * single-unit orders.
 */
export function replicateIntoParcels(
  contents: ConsignmentParcelItemApiModel[],
  parcelCount: number,
  opts: DistributeOptions
): ConsignmentApiModel {
  const plan = buildParcels(Math.max(parcelCount, 0), opts);

  plan.Parcels.forEach((parcel, index) => {
    parcel.Contents = contents.map((row) => {
      const split = QuantityValueSummary(row.Value, parcelCount);
      return {
        ...row,
        Id: null,
        UID: tinyId(),
        Value: round2(index === 0 ? split.valueForFirstItem : split.valueForOtherItems)
      };
    });
  });

  return finalise(plan, opts);
}

/**
 * Divides `total` across caller-supplied per-box quantities, so the box values always sum to the
 * total no matter how the user has redistributed quantity. Box 0 absorbs the rounding remainder.
 */
export function splitValueAcross(total: number, quantities: number[]): number[] {
  const totalQuantity = sum(quantities);

  if (!(totalQuantity > 0)) {
    return quantities.map(() => 0);
  }

  const perUnit = QuantityValueSummary(total, totalQuantity);

  return quantities.map((quantity, index) => {
    if (!(quantity > 0)) {
      return 0;
    }
    const first = index === 0 ? perUnit.valueForFirstItem : perUnit.valueForOtherItems;
    return round2(first + perUnit.valueForOtherItems * (quantity - 1));
  });
}

/**
 * Per-unit weight for each box, from the ORIGINAL quantity rather than the boxes' current total.
 *
 * Deliberately NOT `splitValueAcross`, despite the identical shape. That function conserves its total
 * however the user redistributes quantity, because value is a customs declaration and must not drift.
 * Weight is physical: if three of four ordered units are shipped, those three weigh three quarters of the
 * original, and the price should say so. So a quantity mismatch legitimately reduces the total weight
 * while leaving the total value alone.
 *
 * No remainder absorption either, so boxes with equal quantity get equal weights - three boxes of a 10kg
 * item come out 3.33 each, 0.01 short of the original. Harmless: each box becomes an independent shipment
 * priced on its own weight, so unlike a consignment there is no parcel-sum invariant to satisfy.
 */
export function splitWeightByUnit(totalWeight: number, totalQuantity: number, quantities: number[]): number[] {
  // Convert renders before its boxes are seeded, and an item may legitimately have no weight yet.
  if (!(totalWeight > 0) || !(totalQuantity > 0)) {
    return quantities.map(() => 0);
  }

  const perUnit = totalWeight / totalQuantity;

  return quantities.map((quantity) => (quantity > 0 ? round2(perUnit * quantity) : 0));
}

const finalise = (plan: ConsignmentApiModel, opts: DistributeOptions): ConsignmentApiModel => {
  if (opts.splitWeight) {
    SplitConsignmentWeight(opts.dimensions, plan);
  }
  return plan;
};
